import MirrorBackButton from "@/components/MirrorBackButton";
// TODO: Pre-populate nutrition step with totals.calories from useNutrition when nutrition check-in step is added
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import CheckInNextStep from "@/components/checkin/CheckInNextStep";
import MobileLayout from "@/components/MobileLayout";
import { useCheckIn, INT_TO_MOOD, MOOD_TO_INT } from "@/hooks/useCheckIn";
import { sfx } from "@/lib/sfx";
import type { MoodValue, SleepQuality, IntentionTag, CheckInRecord } from "@/hooks/useCheckIn";
import CheckinActionModal, { CheckinPendingActionLauncher } from "@/components/mirror/CheckinActionModal";
import { newCheckinOperationId, snapshotForCheckin, type CheckinActionPrefill } from "@/lib/checkinExecutor";
import type { CheckinSnapshot, CheckinBreathingRun } from "../../supabase/functions/_shared/checkinActionDomain";
import { checkInAnalyticsTime, checkInDayKey } from "@/lib/checkInDates";
import { localeTagFor } from "@/i18n/locale";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { CHECKIN_DASHBOARD, extractCheckinSamples } from "@/lib/analytics/checkinDashboard";
import type { Granularity } from "@/lib/analytics/engine";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useXP } from "@/hooks/useXP";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useOneSignal } from "@/hooks/useOneSignal";
import { PushOptInSheet } from "@/components/PushOptInSheet";
import { PUSH_OPTIN_EVENT } from "@/lib/pushOptinEvents";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Wind, Heart, Sun, Moon, Zap, Brain,
  Target, Users, Book, Coffee, Sparkles, BarChart3, Flame, Check, Pencil,
  Mic, MicOff, History,
} from "lucide-react";
import BreathingCircle from "@/components/BreathingCircle";
import type { BreathPhase } from "@/components/BreathingCircle";
import { useSleep } from "@/hooks/useSleep";
import { sleepDurationValue } from "@/lib/sleep/values";

// ── Constants ────────────────────────────────────────────────────────────────

// Shared by the gratitude textarea's maxLength and by voice dictation, which
// writes state directly and would otherwise bypass the limit.
const GRATITUDE_MAX = 200;

const CHECKIN_STEP_KEYS = [
  "ux.checkin.feelings",
  "checkin.summary.sleep",
  "checkin.summary.intentions",
  "checkin.summary.gratitude",
  "checkin.summary.breathing",
  "ux.checkin.review",
] as const;

const TRANSITION = { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] as const };
const ENTER = { opacity: 0, x: 60 };
const CENTER = { opacity: 1, x: 0 };
const EXIT = { opacity: 0, x: -60 };

const MOOD_OPTIONS: { value: MoodValue; icon: typeof Sparkles; color: string; glow: string }[] = [
  { value: "rad", icon: Sparkles, color: "#00e5ff", glow: "0 0 24px rgba(0,229,255,0.4)" },
  { value: "good", icon: Sun, color: "#00b8a9", glow: "0 0 24px rgba(0,184,169,0.4)" },
  { value: "meh", icon: Coffee, color: "#6b7280", glow: "0 0 24px rgba(107,114,128,0.4)" },
  { value: "low", icon: Moon, color: "#f59e0b", glow: "0 0 24px rgba(245,158,11,0.4)" },
  { value: "awful", icon: Heart, color: "#ef4444", glow: "0 0 24px rgba(239,68,68,0.4)" },
];

const SLEEP_OPTIONS: { value: SleepQuality; icon: typeof Moon }[] = [
  { value: "great", icon: Sparkles },
  { value: "okay", icon: Sun },
  { value: "poor", icon: Moon },
];

const INTENTION_TAGS: { value: IntentionTag; icon: typeof Target }[] = [
  { value: "work", icon: Target },
  { value: "family", icon: Users },
  { value: "health", icon: Heart },
  { value: "learning", icon: Book },
  { value: "rest", icon: Coffee },
  { value: "create", icon: Sparkles },
  { value: "connect", icon: Users },
];

const MOOD_INT_TO_COLOR: Record<number, string> = {
  5: "#00e5ff", 4: "#00b8a9", 3: "#6b7280", 2: "#f59e0b", 1: "#ef4444",
};

const BREATH_PATTERNS = {
  box:        { inhale: 4, hold: 4, exhale: 4, rest: 4, cycle: 16, nameKey: "checkin.breathing.box", descKey: "checkin.breathing.box.desc" },
  relaxing:   { inhale: 4, hold: 7, exhale: 8, rest: 0, cycle: 19, nameKey: "checkin.breathing.relaxing", descKey: "checkin.breathing.relaxing.desc" },
  energizing: { inhale: 4, hold: 2, exhale: 4, rest: 0, cycle: 10, nameKey: "checkin.breathing.energizing", descKey: "checkin.breathing.energizing.desc" },
} as const;

// ── Stats Sub-component ──────────────────────────────────────────────────────

type HistoryLoadState = { loaded: boolean; loading: boolean; error: boolean };

function HistoryLoadStatus({ state, onRetry, t }: {
  state: HistoryLoadState;
  onRetry: () => void;
  t: (key: string) => string;
}) {
  if (state.loading) return (
    <p role="status" className="py-4 text-sm text-muted-foreground">
      {t(state.loaded ? "ux.checkin.historyRefreshing" : "ux.checkin.historyLoading")}
    </p>
  );
  if (!state.error) return null;
  return (
    <div className="my-3 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4">
      <p role="alert" className="text-sm text-foreground">
        {t(state.loaded ? "ux.checkin.historyRefreshError" : "ux.checkin.historyLoadError")}
      </p>
      <button type="button" onClick={onRetry}
        className="mt-3 min-h-11 rounded-xl border border-cyan-400/30 px-4 text-sm text-cyan-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
        {t("ux.checkin.historyRetry")}
      </button>
    </div>
  );
}

const StatsView = ({
  stats,
  history,
  historyState,
  onRetry,
  t,
  onClose,
  serverNow,
}: {
  stats: ReturnType<typeof useCheckIn>["stats"];
  history: CheckInRecord[];
  historyState: HistoryLoadState;
  onRetry: () => void;
  t: (key: string) => string;
  onClose: () => void;
  serverNow: string | undefined;
}) => {
  const dayLabels = [
    t("checkin.sun"), t("checkin.mon"), t("checkin.tue"), t("checkin.wed"),
    t("checkin.thu"), t("checkin.fri"), t("checkin.sat"),
  ];
  // Shared analytics engine — avg mood/energy/sleep by day/week/month + trend +
  // projection. Streak stays in the flame stat above (streak:false in config).
  const [gran, setGran] = useState<Granularity>(CHECKIN_DASHBOARD.defaultGranularity);
  const analytics = useMemo(
    () => buildDashboard(extractCheckinSamples(history), CHECKIN_DASHBOARD, gran, checkInAnalyticsTime(serverNow ?? new Date()) ?? Date.now()),
    [history, gran, serverNow],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={TRANSITION}
      className="space-y-5"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold text-foreground">
          {t("checkin.stats.title")}
        </h2>
        <button onClick={onClose} aria-label={t("common.back")} className="flex min-h-11 min-w-11 items-center justify-center rounded-xl text-muted-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
          <ChevronLeft size={18} />
        </button>
      </div>

      <p className="text-xs text-muted-foreground">{t("checkin.dayBasis")}</p>
      {/* Streak */}
      <div className="rounded-2xl p-4" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
        <div className="flex items-center gap-3">
          <Flame size={22} className="text-orange-400" />
          <div>
            <p className="font-display text-2xl font-bold text-foreground">{stats.streak}</p>
            <p className="text-[11px] text-muted-foreground">{t("checkin.streak.label")}</p>
          </div>
        </div>
      </div>

      {/* Week mood dots */}
      <div className="rounded-2xl p-4" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
        <p className="text-[11px] text-muted-foreground font-display tracking-wider mb-3">
          {t("checkin.stats.moodChart")}
        </p>
        <div className="flex justify-between items-center px-1">
          {stats.weekMoods.map((m, i) => (
            <div key={i} className="flex flex-col items-center gap-2" aria-label={`${dayLabels[i]}: ${m == null ? "—" : t(`checkin.mood.${INT_TO_MOOD[m]}`)}`}>
              <div
                className="w-4 h-4 rounded-full transition-all"
                style={{
                  backgroundColor: m != null ? (MOOD_INT_TO_COLOR[m] ?? "#1a1a2e") : "hsl(220 35% 12%)",
                  boxShadow: m != null ? `0 0 8px ${MOOD_INT_TO_COLOR[m] ?? "transparent"}44` : "none",
                }}
              />
              <span className="text-[9px] font-display text-muted-foreground">{dayLabels[i]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Averages */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl p-4" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
          <p className="text-[10px] text-muted-foreground font-display tracking-wider mb-1">
            {t("checkin.stats.average")} {t("checkin.mood")}
          </p>
          <p className="font-display text-xl font-bold text-foreground">
            {stats.averageMoodThisWeek != null ? stats.averageMoodThisWeek.toFixed(1) : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">{t("checkin.stats.thisWeek")}</p>
        </div>
        <div className="rounded-2xl p-4" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
          <p className="text-[10px] text-muted-foreground font-display tracking-wider mb-1">
            {t("checkin.stats.average")} {t("checkin.energy")}
          </p>
          <p className="font-display text-xl font-bold text-foreground">
            {stats.averageEnergyThisWeek != null ? stats.averageEnergyThisWeek.toFixed(1) : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">{t("checkin.stats.thisWeek")}</p>
        </div>
      </div>

      {/* Evolução — shared analytics dashboard (avg mood/energy/sleep by
          day/week/month + trend + projection). Descriptive only; no clinical
          claim. Complements the 7-day snapshot above with longer-range trend. */}
      <div>
        <p className="text-[11px] text-muted-foreground font-display tracking-wider mb-3">
          {t("checkin.analytics.title")}
        </p>
        <p className="mb-3 text-sm text-muted-foreground">{t("ux.checkin.historyRange")}</p>
        <HistoryLoadStatus state={historyState} onRetry={onRetry} t={t} />
        {historyState.loaded && <AnalyticsDashboard
          results={analytics}
          granularity={gran}
          onGranularityChange={setGran}
          emptyKey="checkin.analytics.empty"
        />}
      </div>

      {/* Top intentions */}
      {stats.topIntentions.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}>
          <p className="text-[10px] text-muted-foreground font-display tracking-wider mb-3">
            {t("checkin.stats.topIntentions")}
          </p>
          <div className="flex flex-wrap gap-2">
            {stats.topIntentions.slice(0, 3).map((item) => (
              <span
                key={item.tag}
                className="px-3 py-1.5 rounded-full text-[11px] font-display font-medium border border-[#00e5ff]/20 text-[#00e5ff]/80"
                style={{ background: "rgba(0,229,255,0.08)" }}
              >
                {t(`checkin.intention.${item.tag}`)} ({item.count})
              </span>
            ))}
          </div>
        </div>
      )}

      {stats.topIntentions.length === 0 && stats.averageMoodThisWeek == null && (
        <p className="text-center text-muted-foreground text-sm py-4">
          {t("checkin.stats.noData")}
        </p>
      )}
    </motion.div>
  );
};

// ── Particles & Glow Orbs ───────────────────────────────────────────────────

const PARTICLE_CONFIGS = Array.from({ length: 18 }, (_, i) => ({
  w: Math.random() * 2 + 1,
  color: i % 3 === 0 ? "#00e5ff" : i % 3 === 1 ? "hsl(265 60% 65%)" : "hsl(220 70% 65%)",
  opacity: Math.random() * 0.18 + 0.05,
  left: `${Math.random() * 100}%`,
  top: `${Math.random() * 100}%`,
  yDrift: -(Math.random() * 30 + 10),
  xDrift: Math.random() * 20 - 10,
  dur: Math.random() * 8 + 6,
  delay: Math.random() * 5,
}));

const CheckInParticles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
    {PARTICLE_CONFIGS.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.15, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
CheckInParticles.displayName = "CheckInParticles";

const CheckInGlowOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
    <motion.div className="absolute w-[300px] h-[300px] rounded-full"
      style={{ top: "-5%", left: "-15%", background: "radial-gradient(circle, hsl(187 72% 30% / 0.22), transparent 70%)" }}
      animate={{ opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
    <motion.div className="absolute w-[250px] h-[250px] rounded-full"
      style={{ top: "45%", right: "-12%", background: "radial-gradient(circle, hsl(265 60% 30% / 0.18), transparent 70%)" }}
      animate={{ opacity: [0.2, 0.4, 0.2] }} transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 3 }} />
    <motion.div className="absolute w-[200px] h-[200px] rounded-full"
      style={{ bottom: "20%", left: "15%", background: "radial-gradient(circle, hsl(187 72% 50% / 0.10), transparent 70%)" }}
      animate={{ opacity: [0.15, 0.30, 0.15] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 5 }} />
  </div>
));
CheckInGlowOrbs.displayName = "CheckInGlowOrbs";

// ── Main Component ───────────────────────────────────────────────────────────

const DailyCheckIn = () => {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const checkin = useCheckIn(t);
  const { todayCheckIn, todayContext, identityGeneration, captureCurrent, isLoading, saveReviewedCheckIn, startBreathing, getHistory, stats, userId, error: loadError, isRefreshing, isSignedOut, lastUpdated, refresh } = checkin;
  const reducedMotion = useReducedMotion();
  const xp = useXP({ guarded: true });
  const { totalXP, level, getLevelProgress } = xp;
  // Two recognizers, one per dictated field. `transcript` is how each hands its
  // text back — startListening() takes no arguments. Both call sites used to
  // pass it a callback, which JavaScript dropped in silence, so the intention
  // and gratitude mics recorded into the void (P0 28/07).
  const { isListening: isListeningIntention, transcript: intentionTranscript, startListening: startListeningIntention, stopListening: stopListeningIntention, supported: voiceSupported } = useVoiceInput(lang);
  const { isListening: isListeningGratitude, transcript: gratitudeTranscript, startListening: startListeningGratitude, stopListening: stopListeningGratitude } = useVoiceInput(lang);

  const stopVoice = useRef({ intention: stopListeningIntention, gratitude: stopListeningGratitude });
  stopVoice.current = { intention: stopListeningIntention, gratitude: stopListeningGratitude };

  // Push opt-in (Track A web only). userId hydrated lazily on first sheet
  // evaluation; the hook itself reads OneSignal SDK state regardless of
  // userId (used only by saveOptInToDb's UPDATE).
  const [pushUserId, setPushUserId] = useState<string | null>(null);
  const { isSupported: pushSupported, requestPermission: pushRequest, saveOptInToDb: pushSave } = useOneSignal(pushUserId);
  const [showPushSheet, setShowPushSheet] = useState(false);

  // ── State ──
  const [step, setStep] = useState(0);
  const [savedReceipt, setSavedReceipt] = useState<{ energy: number; intentions: string[]; intentionText: string } | null>(null);
  const [showStats, setShowStats] = useState(false);

  // ── History view (chronological, per-record edit/delete) ──
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState<CheckInRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const historyRequest = useRef(0);
  const [actionPrefill, setActionPrefill] = useState<CheckinActionPrefill | null>(null);
  const [historyPeriod, setHistoryPeriod] = useState<30 | 90 | 365>(90);
  const touched = useRef(new Set<string>());
  const currentReview = useRef<{ checkin_day: string; expected_snapshot: CheckinSnapshot | null } | null>(null);
  const reviewCurrent = useRef<(() => boolean) | null>(null);
  const [reviewError, setReviewError] = useState(false);

  const reloadHistory = useCallback(async () => {
    const request = ++historyRequest.current, current = captureCurrent();
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const items = await getHistory(365);
      if (request !== historyRequest.current || !current()) return;
      setHistoryItems(items);
      setHistoryLoaded(true);
    } catch {
      if (request === historyRequest.current && current()) setHistoryError(true);
    } finally {
      if (request === historyRequest.current && current()) setHistoryLoading(false);
    }
  }, [getHistory, captureCurrent]);

  const openHistory = useCallback(() => {
    setShowHistory(true);
    reloadHistory();
  }, [reloadHistory]);

  const openStats = useCallback(() => {
    setShowStats(true);
    void reloadHistory();
  }, [reloadHistory]);

  useEffect(() => {
    if (lastUpdated != null && (showHistory || showStats)) void reloadHistory();
  }, [lastUpdated]); // Opening a view already performs its own read.

  const historyState = { loaded: historyLoaded, loading: historyLoading, error: historyError };

  const openHistoryAction = (rec: CheckInRecord, action: "update" | "delete") => {
    const expected = snapshotForCheckin(rec);
    if (!userId || !expected) { setReviewError(true); return; }
    const operation = newCheckinOperationId();
    setActionPrefill({ action, action_id: operation, operation_id: operation, checkin_id: rec.id,
      expected_user_id: userId, expected_snapshot: structuredClone(expected) });
  };
  const beginReview = (record: CheckInRecord | null) => {
    if (!todayContext || !userId) { setReviewError(true); return false; }
    const expected = record ? snapshotForCheckin(record) : null;
    if (record && !expected) { setReviewError(true); return false; }
    currentReview.current = { checkin_day: todayContext.checkin_day, expected_snapshot: expected && structuredClone(expected) };
    touched.current.clear(); reviewCurrent.current = captureCurrent(); setReviewError(false); return true;
  };
  const [mood, setMood] = useState<MoodValue | null>(null);
  const [energy, setEnergy] = useState<number | null>(null);
  const [sleepQuality, setSleepQuality] = useState<SleepQuality | null>(null);
  const { lastNightSleep: sleepData } = useSleep();
  const detectedSleepDuration = sleepDurationValue(sleepData?.duration_hours);
  const detectedSleepMinutes = detectedSleepDuration === null ? null : Math.round(detectedSleepDuration * 60);
  const [sleepAutoDetected, setSleepAutoDetected] = useState(false);
  const [intentions, setIntentions] = useState<IntentionTag[]>([]);
  const [intentionText, setIntentionText] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [breathingCompleted, setBreathingCompleted] = useState(false);
  const [breathingSeconds, setBreathingSeconds] = useState(0);
  const [isBreathing, setIsBreathing] = useState(false);
  const [breathPhase, setBreathPhase] = useState<BreathPhase>("inhale");
  const [breathPhaseSec, setBreathPhaseSec] = useState(0);
  const [breathPattern, setBreathPattern] = useState<"box" | "relaxing" | "energizing">("box");
  const [saving, setSaving] = useState(false);
  const [breathingStarting, setBreathingStarting] = useState(false);
  const [breathingRun, setBreathingRun] = useState<CheckinBreathingRun | null>(null);

  const breathGeneration = useRef(0);
  const breathTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const breathStartRef = useRef<number>(0);
  const breathAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submissionRef = useRef(false);
  const breathingStartBusy = useRef(false);
  const intentionActor = useRef<(() => boolean) | null>(null), gratitudeActor = useRef<(() => boolean) | null>(null);
  const pushActor = useRef<(() => boolean) | null>(null);

  // Leaving breathing cancels both the exercise and its delayed transition.
  useEffect(() => {
    if (step !== 5) { breathGeneration.current++; setIsBreathing(false); }
    return () => {
      if (breathTimerRef.current) clearInterval(breathTimerRef.current);
      if (breathAdvanceRef.current) clearTimeout(breathAdvanceRef.current);
      breathTimerRef.current = null;
      breathAdvanceRef.current = null;
    };
  }, [step]);

  useEffect(() => {
    historyRequest.current += 1;
    setHistoryItems([]); setHistoryLoaded(false); setHistoryError(false); setHistoryLoading(false);
    setActionPrefill(null); setShowHistory(false); setShowStats(false); setStep(0); setSavedReceipt(null);
    setMood(null); setEnergy(null); setSleepQuality(null); setSleepAutoDetected(false);
    setIntentions([]); setIntentionText(""); setGratitude("");
    setBreathingCompleted(false); setBreathingSeconds(0); setIsBreathing(false); setBreathingRun(null);
    currentReview.current = null; reviewCurrent.current = null; intentionActor.current = null; gratitudeActor.current = null; pushActor.current = null;
    stopVoice.current.intention(); stopVoice.current.gratitude(); setSaving(false); setBreathingStarting(false);
    setShowPushSheet(false);
    return () => { historyRequest.current += 1; };
  }, [userId, identityGeneration]);

  // ── Voice dictation → intention / gratitude ──
  // What each field held when its mic opened, so dictation extends what the
  // user already typed instead of wiping it.
  const intentionBaseline = useRef("");
  const gratitudeBaseline = useRef("");
  const mergeIntention = (text: string) =>
    intentionBaseline.current ? `${intentionBaseline.current} ${text}` : text;
  // The textarea caps typing at GRATITUDE_MAX; dictation writes state directly,
  // so it has to honour the same cap rather than slip past it.
  const mergeGratitude = (text: string) =>
    (gratitudeBaseline.current ? `${gratitudeBaseline.current} ${text}` : text).slice(0, GRATITUDE_MAX);

  useEffect(() => {
    if (!intentionTranscript || !intentionActor.current?.()) return;
    touched.current.add("intention_text"); setIntentionText(mergeIntention(intentionTranscript));
  }, [intentionTranscript]);

  useEffect(() => {
    if (!gratitudeTranscript || !gratitudeActor.current?.()) return;
    touched.current.add("gratitude"); setGratitude(mergeGratitude(gratitudeTranscript));
  }, [gratitudeTranscript]);

  const toggleIntentionVoice = () => {
    if (isListeningIntention) {
      const finalText = stopListeningIntention();
      if (finalText && intentionActor.current?.()) { touched.current.add("intention_text"); setIntentionText(mergeIntention(finalText)); }
      return;
    }
    intentionActor.current = captureCurrent();
    intentionBaseline.current = intentionText;
    startListeningIntention();
  };

  const toggleGratitudeVoice = () => {
    if (isListeningGratitude) {
      const finalText = stopListeningGratitude();
      if (finalText && gratitudeActor.current?.()) { touched.current.add("gratitude"); setGratitude(mergeGratitude(finalText)); }
      return;
    }
    gratitudeActor.current = captureCurrent();
    gratitudeBaseline.current = gratitude;
    startListeningGratitude();
  };

  // Auto-populate sleep quality from sleep module
  useEffect(() => {
    if (sleepData && !sleepQuality) {
      const q = sleepData.quality;
      if (q) {
        const mapped: SleepQuality = (q === "amazing" || q === "good") ? "great" : q === "fair" ? "okay" : "poor";
        setSleepQuality(mapped);
        setSleepAutoDetected(true);
      }
    }
  }, [sleepData, sleepQuality]);

  const hour = new Date().getHours();
  const greeting = hour < 12
    ? t("checkin.greeting.morning")
    : hour < 18
      ? t("checkin.greeting.afternoon")
      : t("checkin.greeting.evening");

  // ── Navigation helpers ──
  const canNext = (s: number): boolean => {
    if (s === 1) return mood != null && energy != null;
    return true;
  };

  const goNext = () => setStep((s) => s + 1);
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  // ── Mood color for today's check-in display ──
  const moodColorFromInt = (m: number | null): string => {
    if (m == null) return "#1a1a2e";
    return MOOD_INT_TO_COLOR[m] ?? "#1a1a2e";
  };

  // ── Loading state ──
  if (isLoading) {
    return (
      <MobileLayout className="pb-24 pt-8">
      <MirrorBackButton />
        <div className="flex items-center justify-center min-h-[60vh]">
          <motion.div
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="text-muted-foreground text-sm font-display"
          >
            {t("checkin.scanning")}
          </motion.div>
        </div>
      </MobileLayout>
    );
  }

  if (isSignedOut || (loadError && lastUpdated == null && step === 0)) {
    return <MobileLayout className="pb-24 pt-8">
      <MirrorBackButton />
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col justify-center gap-4 px-5">
        <h1 className="font-display text-2xl font-semibold">{t("nav.checkin")}</h1>
        <p role={isSignedOut ? "status" : "alert"} className="text-sm leading-relaxed text-muted-foreground">
          {t(isSignedOut ? "checkin.signInRequired" : "checkin.loadError")}
        </p>
        <button type="button" disabled={isRefreshing} onClick={() => isSignedOut ? navigate("/auth?mode=signin") : void refresh()}
          className="min-h-11 rounded-xl bg-primary px-4 font-semibold text-primary-foreground focus-visible:ring-2 focus-visible:ring-primary">
          {t(isSignedOut ? "auth.signIn" : "common.retry")}
        </button>
      </div>
    </MobileLayout>;
  }

  return (
    <MobileLayout className="pb-24 pt-8 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 100%)" }}>
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes breatheGlow { 0%, 100% { opacity: 0.4; transform: scale(0.97); } 50% { opacity: 0.7; transform: scale(1.03); } }
        @keyframes checkInPulse { 0%, 100% { box-shadow: 0 0 20px rgba(0,229,255,0.2); } 50% { box-shadow: 0 0 40px rgba(0,229,255,0.4); } }
      `}</style>
      {!reducedMotion && <><CheckInParticles /><CheckInGlowOrbs /></>}
      {loadError && <div role="alert" className="relative z-10 mb-4 rounded-xl border border-primary/25 bg-card p-4 text-sm">
        <p>{t("checkin.refreshError")}</p>
        <button type="button" disabled={isRefreshing} onClick={() => void refresh()} className="mt-2 min-h-11 rounded-lg px-3 text-primary underline focus-visible:ring-2 focus-visible:ring-primary">{t("common.retry")}</button>
      </div>}
      {reviewError && <p role="alert" className="relative z-10 mb-4 text-sm text-amber-300">{t("checkinAction.changed")}</p>}
      {step === 0 && !showStats && !showHistory && <p className="relative z-10 mb-3 text-xs leading-relaxed text-muted-foreground">{t("checkin.dayBasis")}</p>}

      {/* Reviewing answers stays reversible until saving begins. */}
      {!showStats && !showHistory && (
        <MirrorBackButton className="mb-4" disabled={saving}
          onClick={step >= 1 && step <= 6 ? goBack : undefined} />
      )}

      {/* Name each step, including the review before persistence. */}
      {step >= 1 && step <= 6 && (
        <div className="mb-6 space-y-3 relative z-10">
          <div className="text-center" aria-live="polite" aria-atomic="true">
            <p className="text-xs text-muted-foreground font-display">
              {tf("ux.checkin.step", { step: String(step), total: String(CHECKIN_STEP_KEYS.length) })}
            </p>
            <p className="mt-1 text-sm font-display font-semibold text-foreground">
              {t(CHECKIN_STEP_KEYS[step - 1])}
            </p>
          </div>
          <div className="flex justify-center gap-1.5" aria-hidden="true">
            {CHECKIN_STEP_KEYS.map((key, index) => (
              <div
                key={key}
                className={`h-1 rounded-full ${index + 1 === step ? "w-6" : "w-2"} ${index + 1 <= step ? "bg-primary" : "bg-muted"}`}
              />
            ))}
          </div>
          {step >= 2 && step <= 5 && (
            <p className="text-center text-xs leading-relaxed text-muted-foreground">
              {t("ux.checkin.optional")}
            </p>
          )}
        </div>
      )}

      <AnimatePresence mode="wait">
        {/* ── SCREEN 0: ENTRY GUARD ── */}
        {step === 0 && !showStats && !showHistory && (
          <motion.div
            key="step0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={EXIT}
            transition={TRANSITION}
            className="relative z-10"
          >
            {/* XP Bar */}
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 mb-5"
              style={{ background: "linear-gradient(135deg, hsl(187 60% 10%), hsl(220 35% 8%))", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
              <span className="px-2.5 py-1 rounded-full font-display text-[10px] font-bold shrink-0"
                style={{ background: "hsl(187 72% 50% / 0.15)", border: "1px solid hsl(187 72% 50% / 0.35)", color: "hsl(187 72% 65%)" }}>
                {tf("habits.xp.level", { level: String(level) })}
              </span>
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "hsl(218 14% 18%)" }}>
                <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${getLevelProgress()}%` }}
                  transition={{ delay: 0.4, duration: 0.9, ease: "easeOut" }}
                  style={{ background: "linear-gradient(90deg, hsl(187 60% 40%), hsl(187 72% 60%))", boxShadow: "0 0 8px hsl(187 72% 50% / 0.45)" }} />
              </div>
              <span className="text-[9px] font-display shrink-0" style={{ color: "hsl(218 14% 55%)" }}>{totalXP} XP</span>
              <motion.div animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.2, 1] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
                <Sparkles size={12} style={{ color: "hsl(187 72% 60%)" }} />
              </motion.div>
            </motion.div>

            {todayCheckIn ? (
              /* Already checked in */
              <div className="flex flex-col items-center gap-6 pt-8">
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(0,229,255,0.1)",
                    boxShadow: "0 0 30px rgba(0,229,255,0.15)",
                  }}
                >
                  <Check size={36} style={{ color: "#00e5ff" }} />
                </motion.div>

                <div className="text-center">
                  <h2 className="font-display text-xl font-semibold text-foreground mb-2">
                    {t("checkin.alreadyDone")}
                  </h2>
                  {stats.streak > 0 && (
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <Flame size={18} className="text-orange-400" />
                      <span className="font-display text-lg font-bold text-foreground">
                        {stats.streak}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {t("checkin.streak.label")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Today's summary card */}
                <div
                  className="w-full rounded-2xl p-5 space-y-3"
                  style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.22)" }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                      {t("checkin.summary.mood")}
                    </span>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: moodColorFromInt(todayCheckIn.mood) }}
                      />
                      <span className="text-sm font-display text-foreground">
                        {todayCheckIn.mood != null && INT_TO_MOOD[todayCheckIn.mood]
                          ? t(`checkin.mood.${INT_TO_MOOD[todayCheckIn.mood]}`)
                          : "—"}
                      </span>
                    </div>
                  </div>
                  {todayCheckIn.gratitude && (
                    <p className="text-foreground/50 text-[12px] font-body italic border-t border-white/5 pt-2">
                      &ldquo;{todayCheckIn.gratitude}&rdquo;
                    </p>
                  )}
                </div>

                <CheckInNextStep energy={todayCheckIn.energy} intentions={todayCheckIn.intentions as string[] | null} intentionText={todayCheckIn.intention_text} />

                <button
                  onClick={openStats}
                  className="w-full h-12 rounded-xl border border-[#00e5ff]/20 text-[#00e5ff] font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                  style={{ background: "rgba(0,229,255,0.08)" }}
                >
                  <BarChart3 size={14} className="inline mr-2" />
                  {t("checkin.viewStats")}
                </button>

                <button
                  onClick={openHistory}
                  className="w-full h-12 rounded-xl border border-[#00e5ff]/15 text-[#00e5ff]/80 font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                  style={{ background: "transparent" }}
                >
                  <History size={14} className="inline mr-2" />
                  {t("checkin.history.button")}
                </button>

                <button
                  onClick={() => {
                    if (!beginReview(todayCheckIn)) return;
                    if (todayCheckIn) {
                      setMood(todayCheckIn.mood == null ? null : INT_TO_MOOD[todayCheckIn.mood] ?? null);
                      setEnergy(todayCheckIn.energy);
                      setSleepQuality((todayCheckIn.sleep_quality as SleepQuality) ?? null);
                      setSleepAutoDetected(false);
                      setIntentions((todayCheckIn.intentions as IntentionTag[]) ?? []);
                      setIntentionText(todayCheckIn.intention_text ?? "");
                      setGratitude(todayCheckIn.gratitude ?? "");
                      setBreathingCompleted(!!todayCheckIn.breathing_completed);
                      setBreathingSeconds(todayCheckIn.breathing_duration_seconds ?? 0); setBreathingRun(null);
                    }
                    setStep(1);
                  }}
                  className="w-full h-12 rounded-xl border border-[#00e5ff]/15 text-[#00e5ff]/70 font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                  style={{ background: "transparent" }}
                >
                  <Pencil size={14} className="inline mr-2" />
                  {t("checkin.edit")}
                </button>

                <button
                  onClick={() => navigate("/home")}
                  className="w-full h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                >
                  {t("checkin.done")}
                </button>
              </div>
            ) : (
              /* Not checked in — welcome */
              <div className="flex flex-col items-center gap-8 pt-12">
                {stats.streak > 0 && (
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="flex items-center gap-2"
                  >
                    <Flame size={28} className="text-orange-400" />
                    <span className="font-display text-3xl font-bold text-foreground">
                      {stats.streak}
                    </span>
                  </motion.div>
                )}

                <div className="text-center">
                  <h1 className="font-display text-2xl font-semibold text-foreground mb-2">
                    {greeting}
                  </h1>
                  <p className="text-muted-foreground text-sm">{t("checkin.subtitle")}</p>
                </div>

                <motion.button
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  onClick={() => { if (beginReview(null)) setStep(1); }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full h-14 rounded-2xl font-display text-[14px] font-semibold tracking-wide transition-all"
                  style={{
                    background: "linear-gradient(135deg, rgba(0,229,255,0.15), rgba(0,229,255,0.08))",
                    border: "1.5px solid rgba(0,229,255,0.5)",
                    color: "#00e5ff",
                    boxShadow: "0 0 20px rgba(0,229,255,0.2), 0 0 40px rgba(0,229,255,0.08)",
                  }}
                >
                  {t("checkin.begin")}
                </motion.button>

                <button type="button" onClick={openStats} className="flex min-h-11 items-center gap-2 rounded-xl px-4 text-sm font-medium text-primary focus-visible:ring-2 focus-visible:ring-primary">
                  <BarChart3 size={16} />{t("checkin.viewStats")}
                </button>
                <button
                  onClick={openHistory}
                  className="flex items-center gap-1.5 font-display text-[12px] font-medium tracking-wide opacity-70 hover:opacity-100 transition-opacity"
                  style={{ color: "#00e5ff" }}
                >
                  <History size={13} />
                  {t("checkin.history.button")}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Stats overlay */}
        {step === 0 && showStats && (
          <motion.div key="stats" className="relative z-10">
            <StatsView stats={stats} history={historyItems} historyState={historyState} onRetry={reloadHistory} t={t} onClose={() => setShowStats(false)} serverNow={todayContext?.server_now} />
          </motion.div>
        )}

        {/* History overlay — chronological list, per-record edit/delete+undo */}
        {step === 0 && showHistory && (
          <motion.div
            key="history"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => setShowHistory(false)}
                className="min-w-11 min-h-11 rounded-xl flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 20%)" }}
                aria-label={t("common.back")}
              >
                <ChevronLeft size={16} className="text-foreground" />
              </button>
              <h2 className="font-display text-lg font-semibold text-foreground">
                {t("checkin.history.title")}
              </h2>
            </div>
            <p className="mb-2 text-xs text-muted-foreground">{t("checkin.dayBasis")}</p>
            <p className="mb-3 text-sm text-muted-foreground">{t("ux.checkin.historyRange")}</p>
            <HistoryLoadStatus state={historyState} onRetry={reloadHistory} t={t} />
            {/* Once loaded, keep even an empty list mounted during refresh/error
                so an open undo window is never unmount-flushed. */}
            {historyLoaded && (
              <div>
                <div className="mb-3 flex gap-2">{([30, 90, 365] as const).map(days => <button type="button" key={days} aria-pressed={historyPeriod === days} onClick={() => setHistoryPeriod(days)} className="min-h-11 rounded-full border border-border px-3 text-xs">{t(days === 365 ? "ux.checkin.historyPeriod365" : "moduleHistory.period" + days)}</button>)}</div>
                <button type="button" className="mb-4 min-h-11 rounded-xl border border-primary/30 px-4 text-sm text-primary" onClick={() => {
                  if (!userId) return; const operation = newCheckinOperationId();
                  setActionPrefill({ action: "create_past", action_id: operation, operation_id: operation, expected_user_id: userId });
                }}>{t("checkinAction.addPast")}</button>
                <div className="space-y-2">{historyItems.filter(r => r.checked_in_at == null || (checkInDayKey(r.checked_in_at) ?? "") >= (checkInDayKey(Date.parse(todayContext?.server_now ?? new Date().toISOString()) - historyPeriod * 86_400_000) ?? "")).map(r => <div key={r.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-xs text-muted-foreground">{r.checked_in_at == null ? t("checkinAction.unknownDay") : new Date(r.checked_in_at).toLocaleDateString(localeTagFor(lang), { timeZone: "America/Sao_Paulo", day: "numeric", month: "short", year: "numeric" })}</p>
                    <p className="text-sm">{r.mood != null && INT_TO_MOOD[r.mood] ? t("checkin.mood." + INT_TO_MOOD[r.mood]) : "—"}{r.energy != null ? " · " + t("checkin.energy") + " " + r.energy + "/5" : ""}</p>
                    {r.gratitude && <p className="mt-1 truncate text-xs italic text-muted-foreground">&ldquo;{r.gratitude}&rdquo;</p>}
                  </div>
                  {snapshotForCheckin(r) ? <><button type="button" aria-label={t("checkin.edit")} onClick={() => openHistoryAction(r, "update")} className="min-h-11 min-w-11 rounded-lg text-primary"><Pencil size={16} className="mx-auto" /></button><button type="button" aria-label={t("common.delete")} onClick={() => openHistoryAction(r, "delete")} className="min-h-11 min-w-11 rounded-lg text-destructive">{t("common.delete")}</button></> : <span className="text-xs text-muted-foreground">{t("checkinAction.readOnly")}</span>}
                </div>)}</div>
                {historyItems.filter(r => r.checked_in_at == null || (checkInDayKey(r.checked_in_at) ?? "") >= (checkInDayKey(Date.parse(todayContext?.server_now ?? new Date().toISOString()) - historyPeriod * 86_400_000) ?? "")).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t("checkin.history.empty")}</p>}
              </div>
            )}
          </motion.div>
        )}

        {/* ── SCREEN 1: MOOD + ENERGY ── */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-8 relative z-10"
          >
            {/* Mood */}
            <div className="text-center">
              <h2 id="checkin-mood-heading" className="font-display text-xl font-semibold text-foreground mb-1">
                {t("checkin.mood.question")}
              </h2>
            </div>

            <div role="group" aria-labelledby="checkin-mood-heading" className="grid grid-cols-5 gap-1.5 sm:gap-3">
              {MOOD_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = mood === opt.value;
                return (
                  <motion.button
                    key={opt.value}
                    type="button"
                    onClick={() => { touched.current.add("mood"); setMood(opt.value); }}
                    aria-pressed={selected}
                    className="flex min-h-11 min-w-0 flex-col items-center gap-2 rounded-xl px-0.5 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    animate={{
                      scale: selected ? 1.15 : mood != null && !selected ? 0.9 : 1,
                      opacity: mood != null && !selected ? 0.75 : 1,
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                  >
                    <div
                      className="w-11 h-11 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300"
                      style={{
                        background: selected ? `${opt.color}22` : "hsl(220 35% 12%)",
                        border: selected ? `2px solid ${opt.color}` : "2px solid transparent",
                        boxShadow: selected ? opt.glow : "none",
                      }}
                    >
                      <Icon size={22} style={{ color: selected ? opt.color : "#6b7280" }} />
                    </div>
                    <span
                      className="w-full break-words text-xs leading-snug font-display font-medium text-foreground"
                      style={selected ? { color: opt.color } : undefined}
                    >
                      {t(`checkin.mood.${opt.value}`)}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            {/* Energy */}
            <div className="space-y-3 pt-2">
              <p id="checkin-energy-heading" className="text-center text-sm text-muted-foreground font-display">
                {t("checkin.energy.question")}
              </p>
              <div role="group" aria-labelledby="checkin-energy-heading" className="grid grid-cols-5 gap-1.5 sm:gap-3">
                {[1, 2, 3, 4, 5].map((level) => {
                  const selected = energy === level;
                  const filled = energy != null && level <= energy;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => { touched.current.add("energy"); setEnergy(level); }}
                      aria-pressed={selected}
                      className="flex min-h-11 min-w-0 flex-col items-center gap-1.5 rounded-xl px-0.5 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <motion.div
                        animate={{
                          scale: selected ? 1.2 : 1,
                          backgroundColor: filled ? "#00e5ff" : "hsl(220 35% 12%)",
                        }}
                        transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        className="w-10 h-10 rounded-full border transition-all duration-200"
                        style={{
                          borderColor: filled ? "#00e5ff" : "rgba(255,255,255,0.08)",
                          boxShadow: filled ? "0 0 12px rgba(0,229,255,0.3)" : "none",
                        }}
                      />
                      <span className="w-full break-words text-xs leading-snug text-foreground font-display">
                        {t(`checkin.energy.${level}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Next */}
            <button
              onClick={goNext}
              disabled={!canNext(1)}
              className="w-full h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97] disabled:opacity-30 disabled:cursor-not-allowed"
              style={{
                background: canNext(1) ? "rgba(0,229,255,0.1)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${canNext(1) ? "rgba(0,229,255,0.3)" : "rgba(255,255,255,0.05)"}`,
                color: "#00e5ff",
              }}
            >
              {t("checkin.next")}
              <ChevronRight size={14} className="inline ml-1" />
            </button>
          </motion.div>
        )}

        {/* ── SCREEN 2: SLEEP ── */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-8 relative z-10"
          >
            <div className="text-center">
              <h2 id="checkin-sleep-heading" className="font-display text-xl font-semibold text-foreground">
                {t("checkin.sleep.question")}
              </h2>
            </div>

            {sleepAutoDetected && sleepData && (
              <div className="mx-auto max-w-xs px-4 py-3 rounded-2xl text-center" style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
                <p className="font-display text-[13px] font-semibold" style={{ color: "#00e5ff" }}>
                  {detectedSleepMinutes === null ? t("sleep.durationMissing") : tf("checkin.sleep.auto", { hours: String(Math.floor(detectedSleepMinutes / 60)), minutes: String(detectedSleepMinutes % 60) })}
                </p>
              </div>
            )}

            <div role="group" aria-labelledby="checkin-sleep-heading" className="grid grid-cols-3 gap-2 sm:gap-4">
              {SLEEP_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = sleepQuality === opt.value;
                return (
                  <motion.button
                    key={opt.value}
                    type="button"
                    onClick={() => { touched.current.add("sleep_quality"); setSleepQuality(opt.value); setSleepAutoDetected(false); }}
                    aria-pressed={selected}
                    whileTap={{ scale: 0.95 }}
                    className="flex min-h-11 min-w-0 flex-col items-center gap-3 px-2 py-5 rounded-2xl border transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    style={{
                      background: selected ? "rgba(0,229,255,0.08)" : "hsl(220 35% 12%)",
                      borderColor: selected ? "#00e5ff" : "rgba(255,255,255,0.05)",
                      boxShadow: selected ? "0 0 20px rgba(0,229,255,0.15)" : "none",
                    }}
                  >
                    <Icon size={26} style={{ color: selected ? "#00e5ff" : "#6b7280" }} />
                    <span
                      className="w-full break-words text-xs leading-snug font-display font-medium text-foreground"
                      style={selected ? { color: "#00e5ff" } : undefined}
                    >
                      {t(`checkin.sleep.${opt.value}`)}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
              >
                {t("checkin.skip")}
              </button>
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: "rgba(0,229,255,0.1)",
                  border: "1px solid rgba(0,229,255,0.3)",
                  color: "#00e5ff",
                }}
              >
                {t("checkin.next")}
                <ChevronRight size={14} className="inline ml-1" />
              </button>
            </div>
          </motion.div>
        )}

        {/* ── SCREEN 3: INTENTION ── */}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-6 relative z-10"
          >
            <div className="text-center">
              <h2 id="checkin-intentions-heading" className="font-display text-xl font-semibold text-foreground">
                {t("checkin.intention.question")}
              </h2>
            </div>

            <p id="checkin-intentions-limit" className="text-center text-sm text-muted-foreground" aria-live="polite">
              {tf("ux.checkin.intentionLimit", { count: String(intentions.length) })}
            </p>
            <div role="group" aria-labelledby="checkin-intentions-heading" aria-describedby="checkin-intentions-limit" className="flex flex-wrap justify-center gap-2.5">
              {INTENTION_TAGS.map((tag) => {
                const Icon = tag.icon;
                const selected = intentions.includes(tag.value);
                return (
                  <motion.button
                    key={tag.value}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => {
                      touched.current.add("intentions"); setIntentions((prev) => {
                        if (selected) return prev.filter((t) => t !== tag.value);
                        if (prev.length >= 3) return prev;
                        return [...prev, tag.value];
                      });
                    }}
                    whileTap={{ scale: 0.95 }}
                    className="flex min-h-11 items-center gap-2 px-4 py-2.5 rounded-full border transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    style={{
                      background: selected ? "rgba(0,229,255,0.12)" : "hsl(220 35% 12%)",
                      borderColor: selected ? "#00e5ff" : "rgba(255,255,255,0.06)",
                    }}
                  >
                    <Icon size={14} style={{ color: selected ? "#00e5ff" : "#6b7280" }} />
                    <span
                      className="text-[12px] font-display font-medium"
                      style={{ color: selected ? "#00e5ff" : "#9ca3af" }}
                    >
                      {t(`checkin.intention.${tag.value}`)}
                    </span>
                  </motion.button>
                );
              })}
            </div>

            <div className="space-y-2">
              <label htmlFor="checkin-intention-text" className="block text-sm font-display text-foreground">
                {t("ux.checkin.intentionLabel")}
              </label>
              <div className="relative flex items-center gap-2">
                <input id="checkin-intention-text" type="text" value={intentionText}
                  onChange={(e) => { touched.current.add("intention_text"); setIntentionText(e.target.value); }}
                  placeholder={t("checkin.intention.placeholder")}
                  className="min-w-0 flex-1 bg-transparent border-0 border-b rounded-lg px-2 py-3 text-[14px] font-body text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all"
                  style={{ borderColor: "rgba(0,229,255,0.15)" }} />
                {voiceSupported && (
                  <motion.button type="button"
                    onClick={toggleIntentionVoice}
                    animate={isListeningIntention ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                    transition={isListeningIntention ? { duration: 0.8, repeat: Infinity } : {}}
                    className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: isListeningIntention ? "hsl(0 70% 50% / 0.20)" : "rgba(0,229,255,0.10)", border: `1px solid ${isListeningIntention ? "hsl(0 70% 55% / 0.50)" : "rgba(0,229,255,0.25)"}`, boxShadow: isListeningIntention ? "0 0 16px hsl(0 70% 50% / 0.35)" : "none" }}>
                    {isListeningIntention ? <MicOff size={13} style={{ color: "hsl(0 70% 65%)" }} /> : <Mic size={13} style={{ color: "#00e5ff" }} />}
                  </motion.button>
                )}
              </div>
            </div>
            <AnimatePresence>
              {isListeningIntention && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex items-center gap-2 mt-1">
                  <div className="flex gap-1 items-end">
                    {[0,1,2].map(i => (<motion.div key={i} className="w-1 rounded-full" style={{ background: "hsl(0 70% 60%)" }} animate={{ height: ["4px","12px","4px"] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />))}
                  </div>
                  <span className="text-[10px] font-display" style={{ color: "hsl(0 70% 65%)" }}>{t("notes.voice.listening")}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3">
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
              >
                {t("checkin.skip")}
              </button>
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: "rgba(0,229,255,0.1)",
                  border: "1px solid rgba(0,229,255,0.3)",
                  color: "#00e5ff",
                }}
              >
                {t("checkin.next")}
                <ChevronRight size={14} className="inline ml-1" />
              </button>
            </div>
          </motion.div>
        )}

        {/* ── SCREEN 4: GRATITUDE ── */}
        {step === 4 && (
          <motion.div
            key="step4"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-8 relative z-10 pt-8"
          >
            <div className="text-center">
              <Heart size={28} className="mx-auto mb-4" style={{ color: "#00e5ff", opacity: 0.6 }} />
              <h2 className="font-display text-xl font-semibold text-foreground">
                {t("checkin.gratitude.question")}
              </h2>
            </div>

            <div className="space-y-2">
              <label htmlFor="checkin-gratitude-text" className="block text-sm font-display text-foreground">
                {t("ux.checkin.gratitudeLabel")}
              </label>
              <div className="relative">
                <textarea
                  id="checkin-gratitude-text"
                  value={gratitude}
                  onChange={(e) => { touched.current.add("gratitude"); setGratitude(e.target.value); }}
                  placeholder={t("checkin.gratitude.placeholder")}
                  rows={3}
                  maxLength={GRATITUDE_MAX}
                  className="w-full rounded-xl p-4 pr-12 text-[14px] font-body text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary resize-none transition-all"
                  style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid rgba(0,229,255,0.15)" }}
                />
                {voiceSupported && (
                  <motion.button type="button"
                    onClick={toggleGratitudeVoice}
                    animate={isListeningGratitude ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                    transition={isListeningGratitude ? { duration: 0.8, repeat: Infinity } : {}}
                    className="absolute top-3 right-3 w-8 h-8 rounded-xl flex items-center justify-center"
                    style={{ background: isListeningGratitude ? "hsl(0 70% 50% / 0.20)" : "rgba(0,229,255,0.10)", border: `1px solid ${isListeningGratitude ? "hsl(0 70% 55% / 0.50)" : "rgba(0,229,255,0.25)"}`, boxShadow: isListeningGratitude ? "0 0 16px hsl(0 70% 50% / 0.35)" : "none" }}>
                    {isListeningGratitude ? <MicOff size={12} style={{ color: "hsl(0 70% 65%)" }} /> : <Mic size={12} style={{ color: "#00e5ff" }} />}
                  </motion.button>
                )}
              </div>
            </div>
            <AnimatePresence>
              {isListeningGratitude && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex items-center gap-2 -mt-2">
                  <div className="flex gap-1 items-end">
                    {[0,1,2].map(i => (<motion.div key={i} className="w-1 rounded-full" style={{ background: "hsl(0 70% 60%)" }} animate={{ height: ["4px","12px","4px"] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />))}
                  </div>
                  <span className="text-[10px] font-display" style={{ color: "hsl(0 70% 65%)" }}>{t("notes.voice.listening")}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3">
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
              >
                {t("checkin.skip")}
              </button>
              <button
                onClick={goNext}
                className="flex-1 h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                style={{
                  background: "rgba(0,229,255,0.1)",
                  border: "1px solid rgba(0,229,255,0.3)",
                  color: "#00e5ff",
                }}
              >
                {t("checkin.next")}
                <ChevronRight size={14} className="inline ml-1" />
              </button>
            </div>
          </motion.div>
        )}

        {/* ── SCREEN 5: BREATHING ── */}
        {step === 5 && (
          <motion.div
            key="step5"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-6 relative z-10 pt-4"
          >
            <div className="text-center">
              <h2 className="font-display text-xl font-semibold text-foreground">
                {t("checkin.breathing.question")}
              </h2>
            </div>

            {/* Active breathing animation */}
            {(isBreathing || breathingCompleted) && (
              <div className="flex flex-col items-center gap-4 py-6">
                <BreathingCircle
                  phase={isBreathing ? breathPhase : "idle"}
                  phaseSec={breathPhaseSec}
                  label={
                    !isBreathing
                      ? t("checkin.breathing.complete")
                      : breathPhase === "inhale" ? t("checkin.breathing.inhale")
                      : breathPhase === "hold" ? t("checkin.breathing.hold")
                      : breathPhase === "exhale" ? t("checkin.breathing.exhale")
                      : breathPhase === "rest" ? t("checkin.breathing.rest")
                      : ""
                  }
                />

                {/* Total countdown */}
                {isBreathing && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-muted-foreground text-sm font-mono"
                  >
                    {Math.floor(Math.max(0, 120 - breathingSeconds) / 60)}:{String(Math.max(0, 120 - breathingSeconds) % 60).padStart(2, "0")}
                  </motion.p>
                )}
              </div>
            )}

            {/* Pattern selection (before starting, not yet completed) */}
            {!isBreathing && !breathingCompleted && (
              <div className="space-y-3">
                {(["box", "relaxing", "energizing"] as const).map((key) => {
                  const pat = BREATH_PATTERNS[key];
                  const selected = breathPattern === key;
                  const timing = pat.rest > 0
                    ? `${pat.inhale}-${pat.hold}-${pat.exhale}-${pat.rest}`
                    : `${pat.inhale}-${pat.hold}-${pat.exhale}`;
                  return (
                    <motion.button
                      key={key}
                      onClick={() => setBreathPattern(key)}
                      aria-pressed={selected}
                      whileTap={{ scale: 0.97 }}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl border transition-all duration-200 text-left"
                      style={{
                        background: selected ? "rgba(0,229,255,0.08)" : "hsl(220 35% 12%)",
                        borderColor: selected ? "#00e5ff" : "rgba(255,255,255,0.05)",
                        boxShadow: selected ? "0 0 16px rgba(0,229,255,0.12)" : "none",
                      }}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          background: selected ? "rgba(0,229,255,0.15)" : "rgba(255,255,255,0.04)",
                          border: `1.5px solid ${selected ? "#00e5ff" : "rgba(255,255,255,0.08)"}`,
                        }}
                      >
                        <Wind size={18} style={{ color: selected ? "#00e5ff" : "#6b7280" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="font-display text-[13px] font-semibold"
                          style={{ color: selected ? "#00e5ff" : "#e5e7eb" }}
                        >
                          {t(pat.nameKey)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">{t(pat.descKey)}</p>
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground shrink-0">{timing}</span>
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Start / Skip buttons (before starting) */}
            {!isBreathing && !breathingCompleted && (
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setBreathingCompleted(false);
                    setBreathingSeconds(0);
                    goNext();
                  }}
                  className="flex-1 h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                >
                  {t("checkin.breathing.skip")}
                </button>
                <button
                  disabled={breathingStarting || !todayContext}
                  onClick={async () => {
                    if (breathingStartBusy.current || !currentReview.current || !reviewCurrent.current?.()) return;
                    const actor = captureCurrent(), exercise = ++breathGeneration.current, current = () => actor() && exercise === breathGeneration.current, day = currentReview.current.checkin_day, pat = BREATH_PATTERNS[breathPattern];
                    breathingStartBusy.current = true; setBreathingStarting(true);
                    try {
                      const run = await startBreathing(day);
                      if (!current()) return;
                      if (!run) { toast.error(t("checkinAction.breathingStartError")); return; }
                      touched.current.add("breathing_completed"); touched.current.add("breathing_duration_seconds"); setBreathingRun(run); setIsBreathing(true); setBreathingSeconds(0);
                      setBreathPhaseSec(pat.inhale); setBreathPhase("inhale");
                      // A monotonic local estimate animates the exercise. The server validates the claimed run's elapsed time.
                      breathStartRef.current = performance.now();
                      breathTimerRef.current = setInterval(() => {
                        if (!current()) { if (breathTimerRef.current) clearInterval(breathTimerRef.current); return; }
                        const elapsed = Math.floor((performance.now() - breathStartRef.current) / 1000);
                        setBreathingSeconds(elapsed);
                        const cyclePos = elapsed % pat.cycle, inhaleEnd = pat.inhale, holdEnd = inhaleEnd + pat.hold, exhaleEnd = holdEnd + pat.exhale;
                        if (cyclePos < inhaleEnd) { setBreathPhase("inhale"); setBreathPhaseSec(inhaleEnd - cyclePos); }
                        else if (cyclePos < holdEnd) { setBreathPhase("hold"); setBreathPhaseSec(holdEnd - cyclePos); }
                        else if (cyclePos < exhaleEnd) { setBreathPhase("exhale"); setBreathPhaseSec(exhaleEnd - cyclePos); }
                        else { setBreathPhase("rest"); setBreathPhaseSec(pat.cycle - cyclePos); }
                        if (elapsed >= 120) {
                          if (breathTimerRef.current) clearInterval(breathTimerRef.current);
                          setIsBreathing(false); setBreathingCompleted(true); setBreathingSeconds(120);
                          toast.success(t("checkin.breathing.complete"));
                          breathAdvanceRef.current = setTimeout(() => { if (current()) setStep(value => value === 5 ? 6 : value); }, 800);
                        }
                      }, 250);
                    } catch { if (current()) toast.error(t("checkinAction.breathingStartError")); }
                    finally { breathingStartBusy.current = false; if (actor()) setBreathingStarting(false); }
                  }}
                  className="flex-1 h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                  style={{
                    background: "rgba(0,229,255,0.1)",
                    border: "1px solid rgba(0,229,255,0.3)",
                    color: "#00e5ff",
                  }}
                >
                  {t(breathingStarting ? "checkin.recording" : "checkin.breathing.start")}
                </button>
              </div>
            )}

            {/* Stop button (during breathing) */}
            {isBreathing && (
              <button
                onClick={() => {
                  if (breathTimerRef.current) clearInterval(breathTimerRef.current);
                  const elapsed = Math.floor((performance.now() - breathStartRef.current) / 1000);
                  setIsBreathing(false);
                  setBreathingCompleted(true);
                  setBreathingSeconds(elapsed);
                  goNext();
                }}
                className="w-full h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                {t("checkin.breathing.stop")}
              </button>
            )}

            {/* Completed state — Next + Redo buttons (fixes edit mode bug) */}
            {breathingCompleted && !isBreathing && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
              >
                <p className="text-[#00e5ff] font-display text-sm text-center">{t("checkin.breathing.complete")}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      touched.current.add("breathing_completed"); touched.current.add("breathing_duration_seconds"); setBreathingCompleted(false);
                      setBreathingSeconds(0); setBreathingRun(null);
                    }}
                    className="flex-1 h-12 rounded-xl text-muted-foreground font-display text-[13px] font-medium tracking-wide transition-all active:scale-[0.97]"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
                  >
                    {t("checkin.breathing.redo")}
                  </button>
                  <button
                    onClick={() => { if (breathAdvanceRef.current) clearTimeout(breathAdvanceRef.current); goNext(); }}
                    className="flex-1 h-12 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-[0.97]"
                    style={{
                      background: "rgba(0,229,255,0.1)",
                      border: "1px solid rgba(0,229,255,0.3)",
                      color: "#00e5ff",
                    }}
                  >
                    {t("checkin.next")}
                    <ChevronRight size={14} className="inline ml-1" />
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* ── SCREEN 6: SUMMARY ── */}
        {step === 6 && (
          <motion.div
            key="step6"
            initial={ENTER}
            animate={CENTER}
            exit={EXIT}
            transition={TRANSITION}
            className="space-y-5 relative z-10"
          >
            <div className="text-center pt-2">
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <Sparkles size={28} className="mx-auto mb-3" style={{ color: "#00e5ff" }} />
              </motion.div>
              <h2 className="font-display text-xl font-semibold text-foreground">
                {t("ux.checkin.review")}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {t("ux.checkin.reviewHint")}
              </p>
            </div>

            {/* Summary card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl p-5 space-y-4"
              style={{ background: "linear-gradient(145deg, hsl(220 35% 12%), hsl(225 30% 9%))", border: "1px solid hsl(187 72% 50% / 0.18)" }}
            >
              {/* Mood */}
              {mood && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.summary.mood")}
                  </span>
                  <div className="flex min-w-0 max-w-full items-center gap-2">
                    {(() => {
                      const opt = MOOD_OPTIONS.find((o) => o.value === mood);
                      if (!opt) return null;
                      const Icon = opt.icon;
                      return (
                        <>
                          <Icon size={16} className="shrink-0" style={{ color: opt.color }} />
                          <span className="break-words text-sm font-display text-foreground">
                            {t(`checkin.mood.${mood}`)}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Energy */}
              {energy != null && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.energy")}
                  </span>
                  <div className="flex min-w-0 max-w-full flex-col items-end gap-2 text-right">
                    <span className="break-words text-sm text-foreground font-display">
                      {tf("ux.checkin.energyValue", { value: String(energy) })} · {t(`checkin.energy.${energy}`)}
                    </span>
                    <div className="flex gap-1" aria-hidden="true">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          className="w-2.5 h-2.5 rounded-full"
                          style={{
                            backgroundColor: level <= energy ? "#00e5ff" : "rgba(255,255,255,0.08)",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Sleep */}
              {sleepQuality && (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.summary.sleep")}
                  </span>
                  <span className="text-sm font-display text-foreground">
                    {t(`checkin.sleep.${sleepQuality}`)}
                  </span>
                </div>
              )}

              {/* Intentions */}
              {(intentions.length > 0 || intentionText.trim().length > 0) && (
                <div>
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.summary.intentions")}
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {intentions.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 rounded-full text-[10px] font-display"
                        style={{ background: "rgba(0,229,255,0.08)", color: "#00e5ff", border: "1px solid rgba(0,229,255,0.15)" }}
                      >
                        {t(`checkin.intention.${tag}`)}
                      </span>
                    ))}
                  </div>
                  {intentionText.trim().length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-muted-foreground font-display">
                        {t("ux.checkin.intentionLabel")}
                      </p>
                      <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                        {intentionText}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Gratitude */}
              {gratitude && (
                <div className="border-t border-white/5 pt-3">
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.summary.gratitude")}
                  </span>
                  <p className="text-foreground/60 text-[12px] font-body italic mt-1">
                    &ldquo;{gratitude}&rdquo;
                  </p>
                </div>
              )}

              {/* Breathing */}
              {breathingCompleted && (
                <div className="flex items-center justify-between border-t border-white/5 pt-3">
                  <span className="text-[11px] text-muted-foreground font-display tracking-wider">
                    {t("checkin.summary.breathing")}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Wind size={14} style={{ color: "#00e5ff" }} />
                    <span className="text-sm text-foreground font-display">
                      {breathingSeconds}s
                    </span>
                    <Check size={14} style={{ color: "#00e5ff" }} />
                  </div>
                </div>
              )}
            </motion.div>

            {/* Mirror response */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="rounded-2xl p-4 relative overflow-hidden"
              style={{ background: "linear-gradient(rgba(0,0,0,0.52), rgba(0,0,0,0.70)), url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_016.jpeg') center/cover no-repeat", border: "1px solid rgba(0,229,255,0.22)", boxShadow: "0 0 30px rgba(0,229,255,0.08)" }}
            >
              <motion.div className="absolute top-0 left-0 right-0 h-[1px]"
                animate={{ backgroundPosition: ["0% 50%", "200% 50%"] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                style={{ background: "linear-gradient(90deg, transparent, rgba(0,229,255,0.70), rgba(150,100,255,0.50), rgba(0,229,255,0.70), transparent)", backgroundSize: "200% 100%" }} />
              <div className="flex items-center gap-2 mb-2 relative z-10">
                <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}>
                  <Sparkles size={14} style={{ color: "#00e5ff", opacity: 0.8 }} />
                </motion.div>
                <span className="text-[10px] font-display tracking-[0.18em] uppercase" style={{ color: "rgba(0,229,255,0.65)" }}>
                  {t("checkin.mirrorResponse")}
                </span>
              </div>
              <p className="text-foreground/75 text-[13px] font-body italic leading-relaxed relative z-10">
                {mood === "rad" && t("checkin.summary.moodRad")}
                {mood === "good" && t("checkin.summary.moodGood")}
                {mood === "meh" && t("checkin.summary.moodMeh")}
                {mood === "low" && t("checkin.summary.moodLow")}
                {mood === "awful" && t("checkin.summary.moodAwful")}
              </p>
            </motion.div>

            {/* Done button */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <button
                type="button"
                onClick={async () => {
                  if (!mood || energy == null || submissionRef.current || isListeningIntention || isListeningGratitude || !currentReview.current || !reviewCurrent.current?.()) return;
                  const savingOwner = userId, current = captureCurrent(), original = structuredClone(currentReview.current);
                  if (!savingOwner) return;
                  submissionRef.current = true; setSaving(true);
                  try {
                    const result = await saveReviewedCheckIn({
                      mood, energy,
                      ...(touched.current.has("sleep_quality") || !original.expected_snapshot && sleepQuality ? { sleep_quality: sleepQuality } : {}),
                      ...(touched.current.has("intentions") || !original.expected_snapshot && intentions.length ? { intentions: [...intentions] } : {}),
                      ...(touched.current.has("intention_text") || !original.expected_snapshot && intentionText ? { intention_text: intentionText || null } : {}),
                      ...(touched.current.has("gratitude") || !original.expected_snapshot && gratitude ? { gratitude: gratitude || null } : {}),
                      ...(touched.current.has("breathing_completed") ? { breathing_completed: breathingCompleted, breathing_duration_seconds: breathingCompleted ? breathingSeconds : null } : {}),
                    }, { ...original, ...(breathingCompleted && breathingSeconds >= 60 && breathingRun ? { breathing_run_id: breathingRun.id } : {}) });
                    if (!current()) return;
                    if (!result) { toast.error(t("checkinAction.outcomeUnknown")); return; }
                    setSavedReceipt({ energy, intentions: [...intentions], intentionText }); analytics.checkinCompleted(); sfx.success("module"); toast.success(t("checkin.signalReceived"));
                    const proof = result.xp;
                    if (proof && proof.user_id === savingOwner) {
                      try {
                      for (const award of proof.awards) if (award.inserted && award.amount > 0) toast.success("+" + award.amount + " XP");
                      await xp.refresh({ ownerId: savingOwner, isCurrent: current });
                      if (!current()) return;
                      if (proof.leveled_up) toast.success(t("habits.xp.levelUp") + " " + proof.level);
                      } catch { /* Receipt confirmed the save; an optional XP display refresh may be retried. */ }
                    }
                    let pushSheetShown = false;
                    if (pushSupported) {
                      try {
                        const { data: { user } } = await supabase.auth.getUser();
                        if (!current()) return;
                        if (user?.id === savingOwner) {
                          const { data: prof } = await supabase.from("user_profiles").select("push_opt_in").eq("user_id", user.id).maybeSingle();
                          if (!current()) return;
                          const optIn = (prof as { push_opt_in?: boolean | null } | null)?.push_opt_in;
                          if (optIn == null) { pushActor.current = current; setPushUserId(user.id); setShowPushSheet(true); pushSheetShown = true; }
                        }
                      } catch (cause) { if (current()) Sentry.captureException(cause, { tags: { feature: "push_optin_probe_failed" } }); }
                    }
                    if (current() && !pushSheetShown) setStep(7);
                  } catch { if (current()) toast.error(t("checkinAction.outcomeUnknown")); }
                  finally { submissionRef.current = false; if (current()) setSaving(false); }
                }}
                disabled={saving || isListeningIntention || isListeningGratitude}
                className="w-full min-h-14 px-4 py-3 rounded-2xl font-display text-[14px] font-semibold tracking-wide transition-all active:scale-[0.97] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, hsl(187 60% 25%), hsl(187 72% 35%))",
                  border: "1.5px solid rgba(0,229,255,0.50)",
                  color: "#00e5ff",
                  boxShadow: "0 0 30px rgba(0,229,255,0.20), 0 4px 24px rgba(0,229,255,0.15)",
                  animation: reducedMotion ? "none" : "checkInPulse 3s ease-in-out infinite",
                }}
              >
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)", animation: "shimmer 2.5s ease-in-out infinite" }} />
                <span className="relative z-10">{saving ? t("checkin.recording") : t(currentReview.current?.expected_snapshot ? "ux.checkin.saveChanges" : "ux.checkin.save")}</span>
              </button>
            </motion.div>
          </motion.div>
        )}
        {step === 7 && savedReceipt && <motion.div key="saved-receipt" initial={ENTER} animate={CENTER} exit={EXIT} transition={TRANSITION} className="relative z-10 py-8 space-y-4">
          <CheckInNextStep {...savedReceipt} />
          <button type="button" onClick={() => navigate("/home")} className="w-full min-h-12 rounded-xl border border-border text-sm font-medium focus-visible:ring-2 focus-visible:ring-primary">{t("checkin.done")}</button>
        </motion.div>}
      </AnimatePresence>

      {userId && <CheckinPendingActionLauncher ownerId={userId} busy={saving || breathingStarting || !!actionPrefill}
        onMirrorResume={locator => navigate("/mirror", { state: { checkinActionRecovery: locator } })}
        onConfirmed={async (response, request) => { const current = captureCurrent(); checkin.acceptConfirmedResult(response, request); await refresh(); if (current()) await xp.refresh({ ownerId: userId, isCurrent: current }); }} />}
      <CheckinActionModal open={!!actionPrefill} prefill={actionPrefill} onClose={() => setActionPrefill(null)} onCreated={() => undefined}
        onConfirmed={async (response, request) => { const current = captureCurrent(); checkin.acceptConfirmedResult(response, request); if (response.action === "delete" && response.checkin_id) setHistoryItems(items => items.filter(item => item.id !== response.checkin_id)); await refresh(); if (current()) await reloadHistory(); if (current() && userId) await xp.refresh({ ownerId: userId, isCurrent: current }); }} />

      {/* Track A web push opt-in sheet — fires once on first check-in
          completion when user_profiles.push_opt_in IS NULL. Sheet owns the
          post-resolution return to the saved check-in and its next step after
          choosing accept or decline (or dismissing). */}
      <PushOptInSheet
        open={showPushSheet}
        trigger="checkin"
        onClose={() => {
          if (!pushUserId || !pushActor.current?.()) return;
          // Backdrop / X tap — treat as decline-without-save so we don't
          // pin the user's preference from an accidental dismiss. They'll
          // see the sheet again on the next check-in.
          setShowPushSheet(false);
          try {
            Sentry.captureMessage(PUSH_OPTIN_EVENT.dismissed, {
              level: "info",
              tags: { feature: "push_optin", trigger: "checkin" },
            });
          } catch { /* dev */ }
          setStep(7);
        }}
        onAccept={async () => {
          const sheetOwner = pushUserId;
          if (!sheetOwner || !pushActor.current?.()) return;
          const current = pushActor.current;
          if (!current) return;
          let result: string;
          try { result = await pushRequest({ ownerId: sheetOwner, isCurrent: current }); }
          catch { if (current()) toast.error(t("checkinAction.accountChanged")); return; }
          if (!current()) return;
          try {
            Sentry.captureMessage(
              result === "granted" ? PUSH_OPTIN_EVENT.granted : PUSH_OPTIN_EVENT.denied,
              {
                level: "info",
                tags: { feature: "push_optin", trigger: "checkin" },
              },
            );
          } catch { /* dev */ }
          setShowPushSheet(false);
          setStep(7);
        }}
        onDecline={async () => {
          const sheetOwner = pushUserId;
          if (!sheetOwner || !pushActor.current?.()) return;
          const current = pushActor.current;
          if (!current) return;
          try { await pushSave(false, { ownerId: sheetOwner, isCurrent: current }); }
          catch { if (current()) toast.error(t("checkinAction.accountChanged")); return; }
          if (!current()) return;
          try {
            Sentry.captureMessage(PUSH_OPTIN_EVENT.decline, {
              level: "info",
              tags: { feature: "push_optin", trigger: "checkin" },
            });
          } catch { /* dev */ }
          setShowPushSheet(false);
          setStep(7);
        }}
      />
    </MobileLayout>
  );
};

export default DailyCheckIn;
